import { api, canWrite, query, send } from "../api";
import type { Preview, Routing, Rule } from "../types";
import { actionButton, button, confirmButton, countryName, field, flash, form, h, input, select, show, table, td, th, userLink } from "../ui";

export async function routingPage() {
  const r = await api<Routing>("/v1/admin/routing");
  if (!r) return;
  const write = canWrite();
  const names = r.relays.map((x) => x.name);
  const relayLabel = (n: string) => {
    const x = r.relays.find((y) => y.name === n);
    return x ? `${n}${x.state !== "active" ? ` (${x.state})` : ""}` : n;
  };

  // The rules, edited in place and saved as a whole.
  const rules: Rule[] = r.rules.map((x) => ({ countries: [...x.countries], relays: [...x.relays], note: x.note }));
  const rulesBox = h("div");
  const drawRules = () => {
    rulesBox.replaceChildren(
      ...rules.map((rule, i) => {
        const countries = input({ value: rule.countries.join(", "), placeholder: "Empty: the default rule", "aria-label": "Countries" });
        countries.addEventListener("input", () => (rule.countries = countries.value.split(/[\s,]+/).map((c) => c.trim().toUpperCase()).filter(Boolean)));
        const note = input({ value: rule.note, placeholder: "Note", maxlength: "200", "aria-label": "Note" });
        note.addEventListener("input", () => (rule.note = note.value));
        const pills = h("div", { class: "rule-relays" });
        const drawPills = () => {
          pills.replaceChildren(
            ...rule.relays.map((name, j) =>
              h(
                "span",
                { class: "pill" },
                relayLabel(name),
                write ? button("↑", "", () => (j > 0 && rule.relays.splice(j - 1, 2, rule.relays[j]!, rule.relays[j - 1]!), drawPills())) : null,
                write ? button("↓", "", () => (j < rule.relays.length - 1 && rule.relays.splice(j, 2, rule.relays[j + 1]!, rule.relays[j]!), drawPills())) : null,
                write ? button("×", "", () => (rule.relays.splice(j, 1), drawPills())) : null,
              ),
            ),
            ...(write
              ? [
                  (() => {
                    const left = names.filter((n) => !rule.relays.includes(n));
                    if (!left.length) return h("span", { class: "muted small" }, "every node is in");
                    const add = select({ "aria-label": "Add a relay" }, [["", "Add a node…"], ...left.map((n): [string, string] => [n, relayLabel(n)])]);
                    add.addEventListener("change", () => {
                      if (add.value) rule.relays.push(add.value);
                      drawPills();
                    });
                    return add;
                  })(),
                ]
              : []),
            ...(rule.relays.length ? [] : [h("span", { class: "error" }, "A rule needs at least one node.")]),
          );
        };
        drawPills();
        if (!write) {
          countries.disabled = true;
          note.disabled = true;
        }
        return h(
          "div",
          { class: "rule" },
          h(
            "div",
            { class: "rule-head" },
            h("b", null, `${i + 1}.`),
            h("span", { class: "muted small" }, rule.countries.length ? rule.countries.map((c) => countryName(c)).join(", ") : "default (every other country)"),
            write
              ? h(
                  "div",
                  { class: "tools" },
                  button("↑", "pill-btn", () => (i > 0 && rules.splice(i - 1, 2, rules[i]!, rules[i - 1]!), drawRules())),
                  button("↓", "pill-btn", () => (i < rules.length - 1 && rules.splice(i, 2, rules[i + 1]!, rules[i]!), drawRules())),
                  button("Remove", "btn small danger", () => (rules.splice(i, 1), drawRules())),
                )
              : null,
          ),
          h("div", { class: "fields" }, field("Countries (ISO codes, comma separated)", countries), field("Note", note)),
          pills,
        );
      }),
      ...(rules.length ? [] : [h("p", { class: "muted" }, "No rules: every computer is told every active node.")]),
    );
  };
  drawRules();
  const saveRules = actionButton(
    "Save rules",
    async () => {
      const bad = rules.find((x) => !x.relays.length);
      if (bad) throw new Error("Every rule needs at least one node.");
      const saved = await send<{ rules: Rule[] }>("PUT", "/v1/admin/routing/rules", { rules: rules.map((x) => ({ countries: x.countries, relays: x.relays, note: x.note })) });
      rules.splice(0, rules.length, ...saved.rules.map((x) => ({ countries: [...x.countries], relays: [...x.relays], note: x.note })));
      drawRules();
      flash(`${saved.rules.length} ${saved.rules.length === 1 ? "rule" : "rules"} saved. Computers follow within a minute.`);
    },
    "btn small primary",
  );

  // Pins.
  const pinsBox = h("div");
  const drawPins = async () => {
    const cur = await api<Routing>("/v1/admin/routing");
    if (!cur) return;
    pinsBox.replaceChildren(
      table(
        [th("On"), th("Target"), th("Relays"), th("Note"), th("By"), th("", "num")],
        cur.pins.map((p) =>
          h(
            "tr",
            null,
            td(p.target_kind),
            h("td", null, p.target_kind === "account" ? userLink(p.target_id, p.target_name ?? p.target_id) : (p.target_name ?? "—"), h("span", { class: "muted block mono" }, p.target_id)),
            td(p.relays.join(", "), "mono"),
            td(p.note || "—"),
            td(p.created_by.slice(0, 8), "mono"),
            write
              ? h("td", { class: "num" }, confirmButton("Remove", "Remove the pin", async () => {
                  await send("DELETE", `/v1/admin/routing/pins/${p.target_kind}/${encodeURIComponent(p.target_id)}`);
                  await drawPins();
                  flash("Pin removed.");
                }, "btn small plain"))
              : td("", "num"),
          ),
        ),
        "No pins. Rules decide for everyone.",
      ),
    );
  };
  const pk = select({ "aria-label": "Kind" }, [["account", "Account"], ["device", "Device (computer)"]]);
  const pid = input({ placeholder: "acct_… or dev_…", autocomplete: "off", required: "" });
  const prelays = input({ placeholder: names.slice(0, 2).join(", "), autocomplete: "off", required: "" });
  const pnote = input({ placeholder: "Why", maxlength: "200" });
  const pinForm = form(
    "fields",
    async () => {
      const relays = prelays.value.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
      await send("PUT", `/v1/admin/routing/pins/${pk.value}/${encodeURIComponent(pid.value.trim())}`, { relays, note: pnote.value.trim() });
      pinForm.reset();
      await drawPins();
      flash("Pinned. The computer follows within a minute.");
    },
    field("Pin", pk),
    field("Id", pid),
    field("Relays, in order (comma separated)", prelays),
    field("Note", pnote),
    h("div", null, h("button", { class: "btn small primary", type: "submit" }, "Pin")),
  );

  // Preview: what a computer would be told.
  const pvCountry = input({ placeholder: "DE", maxlength: "2", autocomplete: "off", "aria-label": "Country" });
  const pvDevice = input({ placeholder: "dev_…", autocomplete: "off", "aria-label": "Device id" });
  const pvAccount = input({ placeholder: "acct_…", autocomplete: "off", "aria-label": "Account id" });
  const pvOut = h("p", { class: "muted" }, "Enter a country, a device id or an account id.");
  const preview = form(
    "fields",
    async () => {
      const p = await send<Preview>("GET", `/v1/admin/routing/preview?${query({ country: pvCountry.value.trim().toUpperCase(), device: pvDevice.value.trim(), account: pvAccount.value.trim() })}`);
      pvOut.replaceChildren(h("b", { class: "mono" }, p.relays.join(" → ") || "nothing"), ` via ${p.via}${p.country ? ` (country ${p.country})` : ""}`);
    },
    field("Country", pvCountry),
    field("Device id", pvDevice),
    field("Account id", pvAccount),
    h("div", null, h("button", { class: "btn small plain", type: "submit" }, "Preview")),
  );

  show(
    "Routing",
    "Which relays a computer is told to use, in order. A pin on the computer wins, then a pin on its account, then the first rule naming its country, then the default rule, else every active node. Dark and retired nodes never appear.",
    h("h2", null, "Rules"),
    h("p", { class: "muted small" }, `Nodes: ${r.relays.map((x) => `${x.name} ${x.label}${x.state !== "active" ? ` (${x.state})` : ""}`).join(" · ") || "none"}. Rules are tried top to bottom; at most one rule has no countries (the default).`),
    rulesBox,
    write ? h("div", { class: "actions" }, button("Add a rule", "btn small plain", () => (rules.push({ countries: [], relays: [], note: "" }), drawRules())), saveRules) : null,
    h("h2", null, "Pins"),
    pinsBox,
    write ? pinForm : null,
    h("h2", null, "Preview"),
    preview,
    pvOut,
  );
  await drawPins();
}
